import { createContext, useEffect, useReducer } from 'react';
import { useSearchParams } from "react-router-dom";
import PropTypes from 'prop-types';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { auth0Config } from '../config';
import axios from '../utils/axios';
import Cookies from 'js-cookie';
import gtm from '../utils/gtm';

const retry = require('async-retry');

let auth0Client = null;

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  configSiteData: {}
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, configSiteData } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      configSiteData
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isInitialized: false,
    isAuthenticated: false,
    user: null
  }),
  UPDATE_USER: (state, action) => ({
    ...state,
    user: action.payload
  })
};

const reducer = (state, action) => (handlers[action.type]
  ? handlers[action.type](state, action)
  : state);

const AuthContext = createContext({
  ...initialState,
  platform: 'Auth0',
  loginWithPopup: () => Promise.resolve(),
  loginWithRedirect: () => Promise.resolve(),
  logout: () => Promise.resolve()
});

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [searchParams] = useSearchParams();

  const getUser = async () => {
    const response = await axios.get('/api/v1/auth0-users/');
    return response;
  };

  const retryConfig = {
    retries: 3,
    minTimeout: 1000,
    maxTimeout: 3000,
    randomize: false
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        auth0Client = new Auth0Client({
          cacheLocation: 'localstorage',
          useCookiesForTransactions: true,
          ...auth0Config
        });

        let callbackReturnTo = null;
        if (searchParams.get('code') && searchParams.get('state')) {
          try {
            const { appState } = await auth0Client.handleRedirectCallback();
            callbackReturnTo = appState?.returnTo || '/dashboard/';
            const userAuth0 = await auth0Client.getUser();
            const authType = userAuth0?.[process.env.REACT_APP_AUTH0_RULE_AUTH_TYPE_KEY];
            if (authType === 'signup' || authType === 'login') {
              const callbackUrl = new URL(callbackReturnTo, window.location.origin);
              await gtm.trackEvent('auth_completed', {
                auth_type: authType,
                user_id: userAuth0?.sub,
                page_url: callbackUrl.toString(),
                page_path: callbackUrl.pathname
              });
            }
          } catch (err) {
            console.error('error', err);
          }

          if (callbackReturnTo) {
            window.history.replaceState({}, document.title, callbackReturnTo);
          } else {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }

        const isAuthenticated = await auth0Client.isAuthenticated();

        if (isAuthenticated) {
          let user = null;
          try {
            const accessToken = await auth0Client.getTokenSilently();
            axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            axios.defaults.headers.common['Content-Type'] = 'application/json';

            const response = await retry(getUser, retryConfig);
            user = response.data;

            try {
              const ga_cid = Cookies.get('_ga');
              if (ga_cid) {
                if (ga_cid !== window.localStorage.getItem('ga_cid')) {
                  window.localStorage.setItem('ga_cid', ga_cid);
                  await axios.patch('/api/v1/auth0-users/update-ga-cid/', { ga_cid });
                }
              }
            } catch (err) {
              console.error('error', err);
            }
          } catch (err) {
            console.error('Failed to bootstrap profile data', err);
          }

          let configSiteData = null;
          try {
            const importedSettings = await import(`settings/${process.env.REACT_APP_SETTINGS}`);
            configSiteData = importedSettings.configSiteData;
          } catch (err) {
            console.error('Failed to load site settings', err);
          }

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated,
              user,
              configSiteData
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated,
              user: null,
              configSiteData: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            configSiteData: null
          }
        });
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const loginWithPopup = async (options) => {
    await auth0Client.loginWithPopup(options);

    const isAuthenticated = await auth0Client.isAuthenticated();

    if (isAuthenticated) {
      const accessToken = await auth0Client.getTokenSilently();
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      axios.defaults.headers.common['Content-Type'] = 'application/json';

      const response = await retry(getUser, retryConfig);
      const user = response.data;

      dispatch({
        type: 'LOGIN',
        payload: { user }
      });
    }
  };

  const loginWithRedirect = async (options) => {
    await auth0Client.loginWithRedirect(options);

    const isAuthenticated = await auth0Client.isAuthenticated();

    if (isAuthenticated) {
      const accessToken = await auth0Client.getTokenSilently();
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      axios.defaults.headers.common['Content-Type'] = 'application/json';

      const response = await retry(getUser, retryConfig);
      const user = response.data;

      dispatch({
        type: 'LOGIN',
        payload: { user }
      });
    }
  };

  const logout = async (redirectUri = null) => {
    const returnTo = redirectUri
      || 'https://rpcfast.com/'
      || process.env.REACT_APP_AUTH0_LOGOUT_REDIRECT_URI
      || `${window.location.origin}/login/`;
    await auth0Client.logout({ returnTo });
    delete axios.defaults.headers.common.Authorization;
    dispatch({ type: 'LOGOUT' });
  };

  const refreshUser = async () => {
    if (!state.isAuthenticated || !auth0Client) return null;
    const response = await retry(getUser, retryConfig);
    dispatch({ type: 'UPDATE_USER', payload: response.data });
    return response.data;
  };

  const getAccessToken = async () => auth0Client.getTokenSilently();
  const handleRedirectCallback = async () => auth0Client.handleRedirectCallback();

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'Auth0',
        loginWithPopup,
        loginWithRedirect,
        logout,
        refreshUser,
        handleRedirectCallback,
        getAccessToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
